import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { scrollToSection } from '../utils/smoothScroll';
import useReducedMotion from '../hooks/useReducedMotion';

const words = ['dominare', 'scalare', 'vincere'];

const HeroSection = () => {
  const [displayText, setDisplayText] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const reducedMotion = useReducedMotion();
  const heroRef = useRef(null);
  const [animationActive, setAnimationActive] = useState(true);

  useEffect(() => {
    let inView = true;
    const update = () => setAnimationActive(inView && !document.hidden);
    const observer = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; update(); })
      : null;
    observer?.observe(heroRef.current);
    document.addEventListener('visibilitychange', update);
    update();
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Trigger animations on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setDisplayText(words[0]);
      setIsDeleting(false);
      return;
    }
    if (!animationActive) return;
    const currentWord = words[currentWordIndex];
    const complete = !isDeleting && displayText.length === currentWord.length;
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          // Word complete, wait then start deleting
          setIsDeleting(true);
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          // Move to next word
          setIsDeleting(false);
          setCurrentWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, complete ? 1500 : isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentWordIndex, reducedMotion, animationActive]);

  const scrollToContact = () => {
    scrollToSection('contact');
  };

  return (
    <section id="hero" ref={heroRef} className="relative min-h-screen bg-[#0b114e] overflow-hidden">
      {/* Background Image - Extended on mobile to avoid edge line */}
      <div 
        className="absolute top-0 right-0 h-full w-full lg:w-[55%]"
        style={isMobile ? { left: '-20%', right: 'auto', width: '120%' } : {}}
      >
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url('/media/nicola-hero.webp')`,
            backgroundPosition: isMobile ? '-30% top' : 'center top',
          }}
        />
        {/* Subtle blue tint overlay */}
        <div className="absolute inset-0 bg-[#0b114e]/5" />
        {/* Gradient inside image container */}
        <div className={`absolute inset-0 ${
          isMobile 
            ? 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/80 via-40% to-transparent to-70%' 
            : 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/70 via-40% to-transparent to-80%'
        }`} />
      </div>

      {/* Content - Centered */}
      <div className="relative min-h-screen flex items-center">
        <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            {/* Exclusive Badge - Hidden on mobile */}
            <div 
              className={`hidden sm:inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '200ms' }}
            >
              <span className="w-2 h-2 bg-[#d4883a] rounded-full" />
              <span className="text-white/70 text-sm">Solo per aziende selezionate</span>
            </div>

            {/* Main Heading */}
            <h1 
              className={`mt-[4.5rem] sm:mt-0 text-3xl sm:text-4xl lg:text-7xl font-bold text-white leading-[1.1] mb-6 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ fontFamily: 'Georgia, serif', transitionDelay: '400ms' }}
            >
              Strategie per{' '}
              <span className="text-[#d4883a] inline-block min-w-[180px] sm:min-w-[220px] lg:min-w-[380px]">
                {displayText}
                <span className="animate-pulse">|</span>
              </span>
              <br />
              il tuo mercato
            </h1>

            {/* Subtitle */}
            <p 
              className={`text-white/60 text-base sm:text-lg lg:text-xl mb-8 sm:mb-10 leading-relaxed max-w-xl transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ fontFamily: 'Georgia, serif', transitionDelay: '600ms' }}
            >
              Consulenza di Marketing Strategico per imprenditori ambiziosi che vogliono <strong className="text-white">risultati straordinari</strong>. Non lavoro con tutti. Lavoro con chi vuole vincere.
            </p>

            {/* Trust Indicators - Compact single row on mobile */}
            <div 
              className={`flex items-center gap-2 mb-6 sm:hidden text-white/50 text-xs transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '700ms' }}
            >
              <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
              <span>14+ anni</span>
              <span className="text-[#d4883a]">·</span>
              <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
              <span>€2.5M+ generati</span>
              <span className="text-[#d4883a]">·</span>
              <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
              <span>Max 4 clienti</span>
            </div>

            {/* CTA Buttons */}
            <div 
              className={`flex flex-col sm:flex-row gap-4 mb-8 sm:mb-12 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '800ms' }}
            >
              <button
                onClick={scrollToContact}
                className="bg-[#d4883a] hover:bg-[#d4896a] text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full transition-all flex items-center justify-center gap-2 text-base sm:text-lg hover:scale-105"
              >
                <span className="hidden sm:inline">Verifica se possiamo lavorare insieme</span>
                <span className="sm:hidden">Inizia ora</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Trust Indicators - Desktop only */}
            <div 
              className={`hidden sm:flex sm:flex-row sm:gap-6 text-white/50 text-xs sm:text-sm transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '1000ms' }}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#d4883a]" />
                <span>+14 anni di esperienza</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#d4883a]" />
                <span>€2.5M+ generati per i clienti</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#d4883a]" />
                <span>Solo 4 clienti alla volta</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Quote - Hidden on mobile */}
      <div 
        className={`absolute bottom-8 left-6 lg:left-12 max-w-lg hidden sm:block transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        style={{ transitionDelay: '1200ms' }}
      >
        <div className="border-l-2 border-[#d4883a]/50 pl-4">
          <p className="text-white/40 italic text-sm">
            "La strategia senza esecuzione è un'illusione.<br />
            L'esecuzione senza strategia è caos."
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
